import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiEnvelope } from '../../models/auth.dto';
import {
  ChainPreviewResponse,
  CreateExportRequestRequest,
  ExportRequestChainSubmissionResponse,
  ExportRequestResponse,
  ExportRequestStatus,
  MiddlewareNotificationResponse,
  PendingDecisionsResponse,
  XmlPreviewResponse,
} from '../../models/export-request.dto';

export type ExportRequestDirection = 'incoming' | 'outgoing';

@Injectable({ providedIn: 'root' })
export class ExportRequestService {
  private apiUrl = environment.cbdx_service.api_path;

  constructor(private http: HttpClient) {}

  create(projectId: string, request: CreateExportRequestRequest): Observable<ApiEnvelope<ExportRequestResponse>> {
    return this.http.post<ApiEnvelope<ExportRequestResponse>>(
      `${this.apiUrl}/projects/${projectId}/export-requests`,
      request
    );
  }

  inbox(
    direction: ExportRequestDirection = 'incoming',
    status?: ExportRequestStatus,
    page = 0,
    size = 20
  ): Observable<ApiEnvelope<ExportRequestResponse[]>> {
    let params = new HttpParams().set('direction', direction).set('page', page).set('size', size);
    if (status) {
      params = params.set('status', status);
    }
    return this.http.get<ApiEnvelope<ExportRequestResponse[]>>(`${this.apiUrl}/export-requests`, { params });
  }

  pendingCount(): Observable<ApiEnvelope<PendingDecisionsResponse>> {
    return this.http.get<ApiEnvelope<PendingDecisionsResponse>>(`${this.apiUrl}/export-requests/pending-count`);
  }

  get(id: string): Observable<ApiEnvelope<ExportRequestResponse>> {
    return this.http.get<ApiEnvelope<ExportRequestResponse>>(`${this.apiUrl}/export-requests/${id}`);
  }

  accept(id: string): Observable<ApiEnvelope<ExportRequestResponse>> {
    return this.http.post<ApiEnvelope<ExportRequestResponse>>(`${this.apiUrl}/export-requests/${id}/accept`, {});
  }

  decline(id: string, reason: string): Observable<ApiEnvelope<ExportRequestResponse>> {
    return this.http.post<ApiEnvelope<ExportRequestResponse>>(`${this.apiUrl}/export-requests/${id}/decline`, {
      reason,
    });
  }

  chainPreview(id: string, countryCode?: string): Observable<ApiEnvelope<ChainPreviewResponse>> {
    let params = new HttpParams();
    if (countryCode) {
      params = params.set('countryCode', countryCode);
    }
    return this.http.get<ApiEnvelope<ChainPreviewResponse>>(`${this.apiUrl}/export-requests/${id}/chain-preview`, {
      params,
    });
  }

  xmlPreview(id: string, countryCode?: string): Observable<ApiEnvelope<XmlPreviewResponse>> {
    let params = new HttpParams();
    if (countryCode) {
      params = params.set('countryCode', countryCode);
    }
    return this.http.get<ApiEnvelope<XmlPreviewResponse>>(`${this.apiUrl}/export-requests/${id}/xml-preview`, {
      params,
    });
  }

  /**
   * The same rendering as {@link xmlPreview}, as a real file download — a selection the country's
   * templates cannot render fails the request outright instead of resolving with a report, so
   * callers only need to handle the error path, not inspect `wouldSucceed`.
   */
  downloadXml(id: string, countryCode?: string): Observable<HttpResponse<Blob>> {
    let params = new HttpParams();
    if (countryCode) {
      params = params.set('countryCode', countryCode);
    }
    return this.http.get(`${this.apiUrl}/export-requests/${id}/xml-preview/download`, {
      params,
      responseType: 'blob',
      observe: 'response',
    });
  }

  /** {@link chainPreview}'s mapped `record`, as a real file download rather than an envelope field. */
  downloadJson(id: string, countryCode?: string): Observable<HttpResponse<Blob>> {
    let params = new HttpParams();
    if (countryCode) {
      params = params.set('countryCode', countryCode);
    }
    return this.http.get(`${this.apiUrl}/export-requests/${id}/json-preview/download`, {
      params,
      responseType: 'blob',
      observe: 'response',
    });
  }

  chainSubmissions(id: string): Observable<ApiEnvelope<ExportRequestChainSubmissionResponse[]>> {
    return this.http.get<ApiEnvelope<ExportRequestChainSubmissionResponse[]>>(
      `${this.apiUrl}/export-requests/${id}/chain-submissions`
    );
  }

  retryChainSubmission(id: string): Observable<ApiEnvelope<ExportRequestChainSubmissionResponse>> {
    return this.http.post<ApiEnvelope<ExportRequestChainSubmissionResponse>>(
      `${this.apiUrl}/export-requests/${id}/chain-submissions/retry`,
      {}
    );
  }

  notifications(id: string): Observable<ApiEnvelope<MiddlewareNotificationResponse[]>> {
    return this.http.get<ApiEnvelope<MiddlewareNotificationResponse[]>>(
      `${this.apiUrl}/export-requests/${id}/notifications`
    );
  }
}
